import React from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

function getScoreColor(score) {
    if (score >= 8) {
        return '#43ac6a';
    } else if (score >= 4 && score < 8) {
        return '#e99002';
    } else {
        return '#f04124';
    }
}
function getConnection(score, num) {
    return (
        <Grid container spacing={6} className="instructions">
            <Grid item xs={12} sm={9} style={{ order: '1' }}>
                <div style={{ padding: '10px' }}>
                    <Typography variant="h6">
                        {num}. Connection  <b style={{ color: getScoreColor(score) }}>({score})</b>
                    </Typography>
                    <Typography variant="subtitle1">
                        <p>
                            <b>What You Want from a Leader</b>
                            <br />
                            The language of Personal Connection means you are interested in more than just working for someone. Trust is built by getting to know your leader as a person and them getting to know you. A leader who works to build a personal relationship with you helps you build a better working relationship. When you feel understood by and personally connected to your leader it boosts your loyalty and desire to go to bat for them. You are more excited to show up when you know your boss knows you as a person, and you feel you know them.
                        </p>
                        <p>
                            But when your leader is all business who takes little interest in you as a person you will start to feel used, just a cog in a system for their purpose. When your leader reveals little about themselves, is cold, or distant, you will struggle to trust them and become less motivated to execute tasks for them.
                        </p>
                        <p>
                            <b>How You Tend to Lead</b>
                            <br />
                            You work hard to communicate with respect, warmth, and consideration of others. You seek to build meaningful relationships with the people you lead. You want to sow the seeds of goodwill to enjoy your work more and help others do the same.
                        </p>
                        <p>
                            <b>If You Scored 3 or Below</b>
                            <br />
                            If you scored low in Personal Connection, you may prefer to keep your work life and your personal life separate. You value <i>boundaries</i> and don't have a need for close friendships at work in order to feel motivated. You also may prefer to respect your leader's personal life as their own and would like the same in return. You may need those boundaries to feel both comfortable and respected.
                        </p>
                        <p>
                            <b>If you lead someone who scored 3 or below</b>, they are less likely to appreciate revealing conversations about life outside of work. You may need to be cautious about inquiring too deeply into their personal life. Ask them if they have a preference for more privacy and if they feel you respect that for them.
                        </p>
                    </Typography>
                </div>

            </Grid>
            <Grid item xs={12} sm={3} style={{ textAlign: 'center', order: '0' }}>
                <img style={{ maxWidth: '160px' }} alt="connection" src="/connection.png" />
            </Grid>
        </Grid>
    );
}
function getMentoring(score, num) {
    return (
        <Grid container spacing={6} className="instructions">
            <Grid item xs={12} sm={9} style={{ order: '1' }}>
                <div style={{ padding: '10px' }}>
                    <Typography variant="h6">
                        {num}. Mentoring  <b style={{ color: getScoreColor(score) }}>({score})</b>
                    </Typography>
                    <Typography variant="subtitle1">
                        <p>
                            <b>What You Want from a Leader</b>
                            <br />
                            When Mentoring is your language, you appreciate a leader who is hands on, who knows more than you and is a good teacher who guides you carefully in your work. You want to be able to learn under your leader. It also helps to know if you make a mistake or have a question, you've got a backstop. When you feel your leader is investing in your development and shows you ways to grow, it increases your enthusiasm and engagement.
                        </p>
                        <p>
                            If your leader doesn't seem interested in helping you grow or develop, you will feel mostly abandoned and adrift. Or if your leader doesn't seem to know what they are doing and can't help you develop, you may resent the fact they get paid more than you or resist their direction and input.
                        </p>
                        <p>
                            <b>How You Tend to Lead</b>
                            <br />
                            As a leader, you like to invest in people development. You don't just think about getting the job done, you want to know how your people are growing and reaching their potential too.
                        </p>
                        <p>
                            <b>If You Scored 3 or Below</b>
                            <br />
                            If you scored low in Mentoring, you may feel you are stronger in your domain than your leader. Perhaps you are a subject matter expert in an area they are not. In this case, you may appreciate <i>empowerment</i> from your leader. You require less guidance and close contact to do your job well. You might prefer the leader give you authorities that might currently belong to them so you can move more quickly and effectively.
                        </p>
                        <p>
                            <b>If you lead someone who scored 3 or below</b>, you may consider exploring whether they feel appropriately empowered to do their job. You could ask if there are any authorities they don't have that they wished they did.
                        </p>
                    </Typography>
                </div>

            </Grid>
            <Grid item xs={12} sm={3} style={{ textAlign: 'center', order: '0' }}>
                <img style={{ maxWidth: '160px' }} alt="mentoring" src="/mentoring.png" />
            </Grid>
        </Grid>
    );
}
function getPurpose(score, num) {
    return (
        <Grid container spacing={6} className="instructions">
            <Grid item xs={12} sm={9} style={{ order: '1' }}>
                <div style={{ padding: '10px' }}>
                    <Typography variant="h6">
                        {num}. Purpose  <b style={{ color: getScoreColor(score) }}>({score})</b>
                    </Typography>
                    <Typography variant="subtitle1">
                        <p>
                            <b>What You Want from a Leader</b>
                            <br />
                            When Higher Purpose is your top score, it means you are mission-driven. You are motivated by a cause larger than financial performance or excellence in work. Making a difference in the world or the lives of people matters to you even in a commercial enterprise. When your leader shares this commitment, or at least understands how important it is to you, that is fuel in your tank. When your leader links your daily work to a larger mission and inspires others to do the same, it boosts your trust in them and your performance.
                        </p>
                        <p>
                            If your leader shows little interest in higher purpose and is only focused on making money or work performance, you will feel uninspired and may lose heart. You may see your leader as myopic or even small minded. This could lead you to search for more meaningful and purpose-driven work environments.
                        </p>
                        <p>
                            <b>How You Tend to Lead</b>
                            <br />
                            As a leader, you tend to be interested in WHY the organization exists beyond just making money. You are likely connected to your own higher purpose and want others to experience the same connection. Inspiring others with a big and meaningful vision comes naturally to you.
                        </p>
                        <p>
                            <b>If You Scored 3 or Below</b>
                            <br />
                            If you scored low in the language of Higher Purpose, your attention may be focused more on <i>performance excellence</i>, winning, and the pursuit of profit. If your leader is focused on higher purpose, you may find it less interesting or even irrelevant to your day to day job. You may appreciate a cleaner separation between doing good in the world and doing well financially. It is possible you experience excessive focus on higher purpose as a distraction from the organization's goals. You may believe the task of serving others is more effectively expressed in charities or non-profit organizations. You are not opposed to doing good, but you may think it fits better in other kinds of organizations.
                        </p>
                        <p>
                            <b>If you lead someone who scored 3 or below</b>, they will be highly motivated by a leader who supports them in their pursuit of excellence and achieving success. If you as a leader are focused on higher purpose, this emphasis will be less powerful in motivating a person who scored low on Higher Purpose.
                        </p>
                    </Typography>
                </div>

            </Grid>
            <Grid item xs={12} sm={3} style={{ textAlign: 'center', order: '0' }}>
                <img style={{ maxWidth: '160px' }} alt="purpose" src="/purpose.png" />
            </Grid>
        </Grid>
    );
}
function getRecognition(score, num) {
    return (
        <Grid container spacing={6} className="instructions">
            <Grid item xs={12} sm={9} style={{ order: '1' }}>
                <div style={{ padding: '10px' }}>
                    <Typography variant="h6">
                        {num}. Recognition  <b style={{ color: getScoreColor(score) }}>({score})</b>
                    </Typography>
                    <Typography variant="subtitle1">
                        <p>
                            <b>What You Want from a Leader</b>
                            <br />
                            For some people, words speak louder than actions. If this is your leadership language, hearing your manager or coworker give you verbal recognition is wind in your sails. Hearing the words, “You did a great job!” is important. Even more powerful? When your leader shares specifically what they saw you achieve or contribute and what it meant to them or the team. This makes you feel seen and appreciated. You work hard and you don't require much from a leader, just some verbal acknowledgement is all it takes to get you motivated.
                        </p>
                        <p>
                            At the same time, if your leader is stingy with verbal praise, this will create resentment and frustration. You'll feel unappreciated. If your leader makes thoughtless or overly critical comments towards you, that's a breach of trust and you will consider work elsewhere.                       
                        </p>
                        <p>
                            <b>How You Tend to Lead</b>
                            <br />
                            If you scored high, as a leader you inspire your team and coworkers with verbal encouragement. You use a variety of verbal means to publicly and privately celebrate their success and contributions. Sometimes this means you tend to avoid critical conversations, however, where clear feedback for improvement are required.                        
                        </p>
                        <p>
                            <b>If You Scored 3 or Below</b>
                            <br />
                            If you scored low, it is likely you don't respond much to verbal encouragement or don't find it useful. You may even feel suspicious if a leader expresses frequent appreciation for all of the things you do well. Instead you may prefer <i>verbal feedback</i> with a focus on areas of improvement. Perhaps you are motivated more by challenge and a leader who is focused on exposing the gaps in your performance as a way to help you get better. 
                        </p>
                        <p>
                            <b>If you lead someone who scored 3 or below</b>, have a conversation with them about this. Ask if they tend to dislike public recognition and instead want you to focus your feedback mostly on the areas they could improve.
                        </p>
                    </Typography>
                </div>

            </Grid>
            <Grid item xs={12} sm={3} style={{ textAlign: 'center', order: '0' }}>
                <img style={{ maxWidth: '160px' }} alt="recognition" src="/recognition.png" />
            </Grid>
        </Grid>
    );
}

function getProximity(score, num) {
    return (
        <Grid container spacing={6} className="instructions">
            <Grid item xs={12} sm={9} style={{ order: '1' }}>
                <div style={{ padding: '10px'}}>
                    <Typography variant="h6">
                        {num}. Proximity  <b style={{ color: getScoreColor(score) }}>({score})</b>
                    </Typography>   
                    <Typography variant="subtitle1">
                        <p>
                            <b>What You Want from a Leader</b>
                            <br/>
                            When your primary leadership language is Proximity, you want to see your leader meaningfully present in the organization. When a leader is accessible and visible, you are more likely to trust their decisions are informed by what's happening on the ground. It helps you do your job when you can see their job. It's also important your leader can see you. This communicates solidarity and support when your leader is interacting with you and sees your contribution. This is as much about a leader's availability, visibility, and access, than simply physical or virtual presence.
                        </p>
                        <p>
                            However, when your leader is invisible for long periods of time, difficult to access, or doesn't seem to understand your role and work, you will lose trust in them. Their credibility falls off a cliff for you. You will doubt their decisions are informed by what's actually happening in the business and no longer buy into their direction.
                        </p>
                        <p>
                            <b>How You Tend to Lead</b>
                            <br/>
                            If you scored high, as a leader you may prioritize being in the office or accessible. You value having your finger on the pulse of what's happening in the trenches. You might have an open-door policy (physical or virtual) or make a point of interacting with your people in their world. This is how you show them you can see what challenges they are up against.
                        </p>
                        <p>
                            <b>If You Scored 3 or Below</b>
                            <br/>
                            If you scored low in this language, it is likely you don't need to see or access your leader very often. You tend to enjoy working independently and prefer high levels of <i>autonomy</i>. You value being trusted and want to extend that same trust upwards. You are generally comfortable with a leader who has low visibility into your work and you may have a low need for line of sight into their work.
                        </p>
                        <p>
                            <b>If you lead someone who scored 3 or below</b>, consider asking them if they feel you give them the right level of autonomy. If they want more independence, and you find it difficult to give, tell them what you need from them in order to give them more autonomy.
                        </p>
                    </Typography> 
                </div>

            </Grid>
            <Grid item xs={12} sm={3} style={{ textAlign: 'center', order: '0' }}>
                <img style={{ maxWidth: '160px' }} alt="proximity" src="/proximity.png" />
            </Grid>
        </Grid>        
    );
}
function Text(props)  {
    switch (props.language) {
        case 'proximity': 
            return getProximity(props.score, props.num);
        case 'connection': 
            return getConnection(props.score, props.num);
        case 'mentoring':
            return getMentoring(props.score, props.num);
        case 'purpose':
            return getPurpose(props.score, props.num);
        case 'recognition':
            return getRecognition(props.score, props.num);
        default:
            break;
    }
}
export default Text;